import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  useColorModeValue,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
} from "@chakra-ui/react";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { checkErrors, hasError } from "../../../../utils/forms-utils";
import { useCustomToast } from "../../../../components/CustomToast/CustomToast";
import { ufs } from "../../../../utils";
import Loading from "../../../../components/Loading/Loading";
import { permitteesPointsService, PointTypeList } from "../../../../services/permitteesPointsService";

const PermitteesTaxiStandModal = ({ isOpen, onClose, onSubmit = null, selectedStand, stands, isEdit, initialData = null, loading = false, onSave }) => {
  const { t } = useTranslation();
  const showToast = useCustomToast();
  const textColor = useColorModeValue("gray.700", "white");
  const [formData, setFormData] = useState(undefined);
  const [inputError, setInputError] = useState({});
  const translationKey = "permittees.taxi-stand";
  const [pracas, setPracas] = useState({});

  const pointTypeList = useMemo(() => [
    PointTypeList.TAXI,
    PointTypeList.MOTOTAXI,
    PointTypeList.ONIBUS,
    PointTypeList.TURISMO,
    PointTypeList.TRANSPORTE_ESCOLAR,
  ], []);

  const ids = useMemo(
    () => ({
      tipo: "type",
      numero: "number",
      capacidade: "capacidade",
      logradouro: "logradouro",
      bairro: "bairro",
      complemento: "complemento",
      municipio: "municipio",
      uf: "uf"
    }),
    []
  );

  const initFormData = (data) => {
    return {
      [ids.tipo]: {
        value: data?.tipo || "",
        validations: [{ required: true }],
      },
      [ids.numero]: {
        value: data?.numero || "",
        validations: [{ required: true }],
      },
      [ids.capacidade]: {
        value: data?.capacidade || 0,
        validations: [],
      },
      [ids.logradouro]: {
        value: data?.logradouro || "",
        validations: [{ required: true }],
      },
      [ids.bairro]: {
        value: data?.bairro || "",
        validations: [],
      },
      [ids.complemento]: {
        value: data?.complemento || "",
        validations: [],
      },
      [ids.municipio]: {
        value: data?.municipio || "",
        validations: [],
      },
      [ids.uf]: {
        value: data?.uf || "",
        validations: [],
      }
    };
  };

  const fetchData = async () => {
    try {
      if (isEdit && selectedStand) {
        const stand = await permitteesPointsService.find(selectedStand);
        setFormData(initFormData(stand));
      }
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  };

  const fetchStands = async () => {
    try {
      const res = await permitteesPointsService.findMany();
      setPracas(res ?? []);
    } catch (error) {
      setPracas([]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchStands();
    if (isEdit && !loading && initialData) {
      return;
    }
    if (!isEdit) {
      setFormData(initFormData());
    }

  }, [loading, initialData, isOpen]);


  const handleInputChange = (e) => {
    const { id, value, type } = e.target;

    let  maskedValue = value;

    maskedValue = type === "checkbox" ? e.target.checked : e.target.value;

    const newFormData = {
      ...formData,
      [id]: { ...formData[id], value: maskedValue },
    };

    setFormData(newFormData);

    let formValidationErrors = checkErrors({ formData: newFormData, inputError: inputError, name: id })

    if (id === ids.numero || id === ids.tipo) {
      if(newFormData[ids.numero].value !== null && newFormData[ids.tipo].value !== null) {
        const valorSelecionado = newFormData[ids.tipo].value;
        const tipo = t(`${translationKey}.form.fields.types.options.${PointTypeList[valorSelecionado]?.labelKey}`);

        if(pracas.some(praca => praca.tipo === tipo && praca.numero === value)) {
          formValidationErrors[ids.numero] = t(`${translationKey}.messages.errors.type-number-already-exists`);
        }
      }
    }

    setInputError(formValidationErrors);
  };

  const handleNumberChange= (name) => (valueAsString, valueAsNumber) => {
    const safeValue = isNaN(valueAsNumber) ? 0 : valueAsNumber;

    const newFormData = {
      ...formData,
      [name]: {
        ...formData[name],
        value: safeValue,
      },
    };

    setFormData(newFormData);
    let formValidationErrors = checkErrors({ formData: newFormData, inputError, name });
    setInputError(formValidationErrors);

    return valueAsNumber;
  };

  const isFormInvalid = () => {
    return formData && inputError ? hasError({ formData, inputError }) : true;
  }

  const handleClose = () => {
    setInputError({});
    onClose();
  }

  const handleSave = async () => {
    try {
      const data = {
        tipo: formData[ids.tipo].value.trim(),
        numero: formData[ids.numero].value.trim(),
        capacidade: formData[ids.capacidade].value,
        logradouro: formData[ids.logradouro].value.trim(),
        bairro: formData[ids.bairro].value.trim(),
        complemento: formData[ids.complemento].value.trim(),
        municipio: formData[ids.municipio].value.trim(),
        uf: formData[ids.uf].value.trim(),
      };

      if (isEdit) {
        onSave(selectedStand, data);
      } else {
        onSave(null, data);
      }

      handleClose();
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  };



  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex direction="column">
            <Text
              color={textColor}
              fontSize="lg"
              fontWeight="bold"
            >
              {isEdit ? t("taxi-stand.form.edit-title") : t("taxi-stand.form.create-title")}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        {formData && !loading ? (
        <ModalBody>
          <Grid templateColumns={{ sm: "1fr", md: "repeat(1, 1fr)" }} gap="24px">
            {/*tipo*/}
            <FormControl isRequired isInvalid={inputError[ids.tipo]}>
              <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                {t("taxi-stand.form.fields.type.label")}
              </FormLabel>
              <Select
                fontSize="sm"
                placeholder={t("taxi-stand.form.fields.type.placeholder")}
                id={ids.tipo}
                name={ids.tipo}
                value={formData[ids.tipo].value}
                onChange={handleInputChange}
                disabled={isEdit}
              >
                {pointTypeList?.map((option, index) => (
                  <option key={index} value={option.value}>
                    {t(`${translationKey}.form.fields.types.options.${option.labelKey}`)}
                  </option>
                ))}
              </Select>

              <FormErrorMessage>
                {t(inputError[ids.tipo])}
              </FormErrorMessage>
            </FormControl>

            {/*numero*/}
            <FormControl isRequired isInvalid={inputError[ids.numero]}>
              <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.number.label")}
              </FormLabel>
              <Input
                id={ids.numero}
                name={ids.numero}
                value={formData[ids.numero].value}
                onChange={handleInputChange}
                placeholder={t("taxi-stand.form.fields.number.placeholder")}
                fontSize="sm"
                disabled={isEdit}
                required
              />
              <FormErrorMessage>
                {t(inputError[ids.numero])}
              </FormErrorMessage>
            </FormControl>

            {/*capacidade*/}
            <FormControl
              color={textColor}
              fontWeight="bold"
              fontSize="xs"
            >
              <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.capacity.label")}
              </FormLabel>
              <NumberInput
                id={ids.capacidade}
                name={ids.capacidade}
                value={formData[ids.capacidade].value ?? 0}
                onChange={handleNumberChange(ids.capacidade)}
                defaultValue={1}
                min={1}
                max={20}
                step={1}
                >
                <NumberInputField fontSize="sm"/>
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>

            </FormControl>

            {/*logradouro*/}
            <FormControl isRequired isInvalid={inputError[ids.logradouro]}>
              <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.location.label")}
              </FormLabel>
              <Input
                id={ids.logradouro}
                name={ids.logradouro}
                value={formData[ids.logradouro].value}
                onChange={handleInputChange}
                placeholder={t("taxi-stand.form.fields.location.placeholder")}
                fontSize="sm"
                required
              />
              <FormErrorMessage>
                {t(inputError[ids.logradouro])}
              </FormErrorMessage>
            </FormControl>

            {/*bairro*/}
            <FormControl>
              <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.neighborhood.label")}
              </FormLabel>
              <Input
                id={ids.bairro}
                name={ids.bairro}
                value={formData[ids.bairro].value}
                onChange={handleInputChange}
                placeholder={t("taxi-stand.form.fields.neighborhood.placeholder")}
                fontSize="sm"
              />
            </FormControl>

            {/*complemento*/}
            <FormControl>
              <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.complement.label")}
              </FormLabel>
              <Input
                id={ids.complemento}
                name={ids.complemento}
                value={formData[ids.complemento].value}
                onChange={handleInputChange}
                placeholder={t("taxi-stand.form.fields.complement.placeholder")}
                fontSize="sm"
              />
            </FormControl>

            {/*municipio*/}
            <FormControl>
              <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.city.label")}
              </FormLabel>
              <Input
                id={ids.municipio}
                name={ids.municipio}
                value={formData[ids.municipio].value}
                onChange={handleInputChange}
                placeholder={t("taxi-stand.form.fields.city.placeholder")}
                fontSize="sm"
              />
            </FormControl>

            {/*UF*/}
            <FormControl isRequired isInvalid={inputError[ids.vehicleUfPlate]}>
            <FormLabel
                color={textColor}
                fontWeight="bold"
                fontSize="xs"
              >
                {t("taxi-stand.form.fields.uf.label")}
              </FormLabel>
              <Select
                  fontSize="sm"
                  id={ids.uf}
                  placeholder={t("taxi-stand.form.fields.uf.placeholder")}
                  value={formData[ids.uf].value.toUpperCase()}
                  onChange={handleInputChange}
              >
                {ufs.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.value} - {uf.label}
                    </option>
                ))}
              </Select>
              <FormErrorMessage>
                {t(inputError[ids.vehicleUfPlate])}
              </FormErrorMessage>
            </FormControl>
          </Grid>
          <Flex w="100%" justify="space-between" mb={4} mt={4}>
            <Button
              variant="no-effects"
              alignSelf="flex-end"
              w="125px"
              bg="gray.100"
              color="gray.700"
              fontSize="sm"
              fontWeight="bold"
              onClick={handleClose}
            >
              {t("system.actions.cancel")}
            </Button>
            <Button
              w="125px"
              variant="dark"
              alignSelf="flex-end"
              type="submit"
              isDisabled={isFormInvalid()}
              onClick={handleSave}
              _hover={isFormInvalid() ? {} : undefined}
              fontSize="sm"
            >
              {t(isEdit ? 'system.actions.edit' : 'system.actions.save')}
            </Button>
          </Flex>
        </ModalBody>
        ) : (
          <Loading h="300px" w="100%" />
        )}
      </ModalContent>
    </Modal>
  );
};

export default PermitteesTaxiStandModal;
